import { useState, useEffect } from 'react';
import AllocationChart from './charts/AllocationChart';
import TrendChart from './charts/TrendChart';
import DistributionChart from './charts/DistributionChart';
import CohortChart from './charts/CohortChart';
import SingleChoiceChart from './charts/SingleChoiceChart';
import { COLORS } from '../utils/colors';
import { useColorScheme } from '../hooks/useColorScheme';
import { downloadCsv } from '../utils/exportCsv';
import PersuadableShare, { buildPersuadableSeries } from './PersuadableShare';
import {
  getAllocationData, getAllocationTrend, getDistribution,
  getCohorts, getCohortsTrend
} from '../utils/api';

/**
 * Renders a question panel with the appropriate chart layout based on question type.
 *
 * Props:
 *   question — { questionId, questionName, questionType, description, startDate, chartFlags }
 *   clientKey — 'az', 'co', etc.
 *   filters — current filter state
 *   filterValueMappings — grouped value mappings for expanding display groups to raw values
 */
export default function QuestionPanel({ question, clientKey, filters, filterValueMappings = {} }) {
  const { palette } = useColorScheme();
  const [allocationData, setAllocationData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [distributionData, setDistributionData] = useState(null);
  const [commitmentData, setCommitmentData] = useState(null);
  const [rejectionData, setRejectionData] = useState(null);
  const [commitmentTrend, setCommitmentTrend] = useState(null);
  const [rejectionTrend, setRejectionTrend] = useState(null);
  const [persOption, setPersOption] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const q = question;
  const flags = q.chartFlags || {};
  const type = q.questionType;

  useEffect(() => {
    if (!q || !clientKey) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    async function fetchData() {
      try {
        const promises = [];

        // Allocation / Slider / Single Choice all need the main data
        if (type === 'allocation' || type === 'slider') {
          promises.push(
            getAllocationData(clientKey, q.questionId, filters, filterValueMappings)
              .then(d => { if (!cancelled) setAllocationData(d); })
          );
        }

        if (type === 'single_choice' || type === 'scale_rating') {
          // For single choice, allocation endpoint returns counts by option
          promises.push(
            getAllocationData(clientKey, q.questionId, filters, filterValueMappings)
              .then(d => { if (!cancelled) setAllocationData(d); })
          );
        }

        // Trendline
        if (flags.showAllocTrend) {
          promises.push(
            getAllocationTrend(clientKey, q.questionId, filters, { trendStartDate: q.trendStartDate, valueMappings: filterValueMappings })
              .then(d => { if (!cancelled) setTrendData(d); })
          );
        }

        // Distribution
        if (flags.showHistogram) {
          promises.push(
            getDistribution(clientKey, q.questionId, filters, filterValueMappings)
              .then(d => { if (!cancelled) setDistributionData(d); })
          );
        }

        // CI/RI
        if (flags.showCIRI) {
          promises.push(
            getCohorts(clientKey, q.questionId, 'commitment', filters, filterValueMappings)
              .then(d => { if (!cancelled) setCommitmentData(d); }),
            getCohorts(clientKey, q.questionId, 'rejection', filters, filterValueMappings)
              .then(d => { if (!cancelled) setRejectionData(d); })
          );
        }

        // CI/RI Trend
        if (flags.showCIRITrend) {
          promises.push(
            getCohortsTrend(clientKey, q.questionId, 'commitment', filters, { trendStartDate: q.trendStartDate, valueMappings: filterValueMappings })
              .then(d => { if (!cancelled) setCommitmentTrend(d); }),
            getCohortsTrend(clientKey, q.questionId, 'rejection', filters, { trendStartDate: q.trendStartDate, valueMappings: filterValueMappings })
              .then(d => { if (!cancelled) setRejectionTrend(d); })
          );
        }

        await Promise.all(promises);
        if (!cancelled) setLoading(false);
      } catch (err) {
        if (!cancelled) {
          setError(err.message);
          setLoading(false);
        }
      }
    }

    fetchData();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q?.questionId, clientKey, JSON.stringify(filters), type]);

  if (loading) {
    return (
      <div style={panelStyle}>
        <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '48px', color: COLORS.textMuted }}>
          Loading chart data...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={panelStyle}>
        <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '48px', color: COLORS.coral }}>
          Error loading data: {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Question header panel */}
      <div style={panelStyle}>
        <div>
          <div style={questionLabelStyle}>Question</div>
          <div style={questionNameStyle}>{q.questionName}</div>
          {q.startDate && (
            <div style={startDateStyle}>
              Tracking since <strong>{formatDate(q.startDate)}</strong>
            </div>
          )}
        </div>
        <div>
          <div style={questionLabelStyle}>Question Text</div>
          <p style={descriptionStyle}>{q.description || ''}</p>
        </div>
      </div>

      {/* Charts grid */}
      <div style={chartsGridStyle}>
        {/* Layout A1: Allocation */}
        {type === 'allocation' && renderAllocationLayout()}

        {/* Layout A2: Slider */}
        {type === 'slider' && renderSliderLayout()}

        {/* Layout B: Single Choice */}
        {type === 'single_choice' && renderSingleChoiceLayout()}

        {/* Layout B: Scale Rating */}
        {type === 'scale_rating' && renderScaleRatingLayout()}
      </div>
    </div>
  );

  function renderAllocationLayout() {
    const hasFilter = Object.values(filters).some(arr => arr.length > 0);
    return (
      <>
        <Card title="Average Allocation" span={2}
              exportData={() => allocationExport(allocationData)}
              exportFilename={`${q.questionName} - Average Allocation`}>
          <Legend items={allocationData?.overall?.map((d, i) => ({
            color: palette[i % palette.length],
            text: d.optionText
          })) || []} />
          <div style={{ height: '220px' }}>
            <AllocationChart data={allocationData} />
          </div>
        </Card>

        {flags.showAllocTrend && trendData && (
          <Card title="Allocation Trendline" span={2}
              exportData={() => trendExport(trendData)}
              exportFilename={`${q.questionName} - Allocation Trendline`}>
            <Legend items={trendData?.series?.map((s, i) => ({
              color: palette[i % palette.length],
              text: s.name
            })) || []} />
            <div style={{ height: '240px' }}>
              <TrendChart data={trendData} />
            </div>
          </Card>
        )}

        {flags.showHistogram && distributionData && (
          <Card title="Distribution of Responses" span={2}
              exportData={() => distributionExport(distributionData)}
              exportFilename={`${q.questionName} - Distribution`}>
            <div style={{ height: '380px' }}>
              <DistributionChart data={distributionData} questionType="allocation" />
            </div>
          </Card>
        )}

        {flags.showCIRI && commitmentData && (
          <Card title="Commitment Index"
              exportData={() => cohortExport(commitmentData, 'commitment')}
              exportFilename={`${q.questionName} - Commitment Index`}>
            <div style={{ height: '200px' }}>
              <CohortChart data={commitmentData} type="commitment" />
            </div>
          </Card>
        )}

        {flags.showCIRI && rejectionData && (
          <Card title="Rejection Index"
              exportData={() => cohortExport(rejectionData, 'rejection')}
              exportFilename={`${q.questionName} - Rejection Index`}>
            <div style={{ height: '200px' }}>
              <CohortChart data={rejectionData} type="rejection" />
            </div>
          </Card>
        )}

        {flags.showCIRITrend && commitmentTrend && rejectionTrend && (() => {
          const persOptions = (commitmentTrend.series || []).map(s => s.name);
          const activeOption = persOption && persOptions.includes(persOption) ? persOption : persOptions[0];
          if (!activeOption) return null;
          return (
            <Card title="Persuadable Share" span={2}
                headerExtra={
                  <OptionPicker options={persOptions} value={activeOption} onChange={setPersOption} />
                }
                exportData={() => persuadableExport(commitmentTrend, rejectionTrend, activeOption)}
                exportFilename={`${q.questionName} - Persuadable Share - ${activeOption}`}>
              <PersuadableShare
                commitmentTrend={commitmentTrend}
                rejectionTrend={rejectionTrend}
                option={activeOption}
              />
            </Card>
          );
        })()}
      </>
    );
  }

  function renderSliderLayout() {
    const overallAvg = allocationData?.overall?.[0]?.avgValue;
    const filteredAvg = allocationData?.filtered?.[0]?.avgValue;
    const hasFilter = Object.values(filters).some(arr => arr.length > 0);

    return (
      <>
        <Card title="Overall Average" span={1}>
          <div style={avgDisplayStyle}>
            <div>
              <div style={{
                ...avgNumberStyle,
                color: COLORS.tealDark,
                textAlign: hasFilter ? 'left' : 'center'
              }}>
                {overallAvg?.toFixed(1) || '—'}
              </div>
              {hasFilter && filteredAvg != null && (
                <div style={{ ...avgNumberStyle, color: COLORS.teal, fontSize: '36px', marginTop: '4px' }}>
                  {filteredAvg.toFixed(1)}
                  <span style={{ fontSize: '14px', color: COLORS.textMuted, marginLeft: '8px' }}>filtered</span>
                </div>
              )}
            </div>
            <div style={avgMetaStyle}>
              <div style={{ fontSize: '14px', color: COLORS.textSecondary }}>out of 100</div>
              <div style={{ fontSize: '12px', color: COLORS.textMuted, marginTop: '4px' }}>Overall Average</div>
            </div>
          </div>
        </Card>

        {flags.showAllocTrend && trendData && (
          <Card title="Average Trendline" span={1}
              exportData={() => trendExport(trendData)}
              exportFilename={`${q.questionName} - Average Trendline`}>
            <div style={{ height: '200px' }}>
              <TrendChart data={trendData} />
            </div>
          </Card>
        )}

        {flags.showHistogram && distributionData && (
          <Card title="Distribution of Responses" span={2}
              exportData={() => distributionExport(distributionData)}
              exportFilename={`${q.questionName} - Distribution`}>
            <div style={{ height: '380px' }}>
              <DistributionChart data={distributionData} questionType="slider" />
            </div>
          </Card>
        )}
      </>
    );
  }

  function renderSingleChoiceLayout() {
    // For single choice, the allocation data contains counts per option
    const chartData = allocationData?.overall?.map(d => ({
      optionText: d.optionText,
      count: d.responseCount
    })) || [];

    return (
      <Card title="% of Respondents" span={2}
          exportData={() => singleChoiceExport(chartData)}
          exportFilename={`${q.questionName} - Respondents`}>
        <Legend items={chartData.map((d, i) => ({
          color: palette[i % palette.length],
          text: d.optionText
        }))} />
        <div style={{ height: Math.max(200, chartData.length * 50) + 'px' }}>
          <SingleChoiceChart data={chartData} />
        </div>
      </Card>
    );
  }

  function renderScaleRatingLayout() {
    // Scale rating shows each option's response rate as a percentage
    const chartData = allocationData?.overall?.map(d => ({
      optionText: d.optionText,
      count: d.responseCount
    })) || [];

    return (
      <Card title="% of Respondents per Option" span={2}
          exportData={() => singleChoiceExport(chartData)}
          exportFilename={`${q.questionName} - Respondents per Option`}>
        <Legend items={chartData.map((d, i) => ({
          color: palette[i % palette.length],
          text: d.optionText
        }))} />
        <div style={{ height: Math.max(200, chartData.length * 50) + 'px' }}>
          <SingleChoiceChart data={chartData} />
        </div>
      </Card>
    );
  }
}

// ── Sub-components ─────────────────────────────────────────────────────────

function Card({ title, span = 1, exportData, exportFilename, headerExtra, children }) {
  const handleExport = () => {
    const d = typeof exportData === 'function' ? exportData() : exportData;
    if (d && d.headers && d.rows) downloadCsv(exportFilename || title, d.headers, d.rows);
  };
  return (
    <div style={{
      ...cardStyle,
      ...(span === 2 ? { gridColumn: '1 / -1' } : {})
    }}>
      <div style={cardHeaderStyle}>
        <div style={cardAccentStyle}></div>
        <div style={cardTitleStyle}>{title}</div>
        {headerExtra}
        {exportData && (
          <button
            style={exportBtnStyle}
            onClick={handleExport}
            title="Download chart values as CSV"
            onMouseEnter={(e) => { e.currentTarget.style.background = '#f7f8fa'; e.currentTarget.style.color = '#1a2332'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#8a94a3'; }}
          >
            CSV &#10515;
          </button>
        )}
      </div>
      <div style={cardBodyStyle}>{children}</div>
    </div>
  );
}

function OptionPicker({ options, value, onChange }) {
  if (!options || options.length < 2) return null;
  if (options.length > 3) {
    return (
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={optionSelectStyle}
        aria-label="Question option"
      >
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  return (
    <div style={optionSegStyle} role="group" aria-label="Question option">
      {options.map(o => (
        <button
          key={o}
          type="button"
          onClick={() => onChange(o)}
          aria-pressed={o === value}
          style={{
            ...optionBtnStyle,
            ...(o === value ? optionBtnActiveStyle : {})
          }}
        >
          {truncateOption(o, 22)}
        </button>
      ))}
    </div>
  );
}

function truncateOption(text, maxLen) {
  if (!text) return '';
  return text.length > maxLen ? text.slice(0, maxLen - 3) + '...' : text;
}

function Legend({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div style={legendStyle}>
      {items.map((item, i) => (
        <div key={i} style={legendItemStyle} title={item.text}>
          <div style={{ ...legendDotStyle, background: item.color }}></div>
          <span style={legendTextStyle}>{item.text}</span>
        </div>
      ))}
    </div>
  );
}

// ── CSV export builders — export ONLY the values shown in the chart ────────

function fmtVal(v) {
  return v == null || isNaN(v) ? '' : Number(v).toFixed(1);
}

function allocationExport(allocationData) {
  const overall = allocationData?.overall || [];
  const hasFiltered = allocationData?.filtered?.length > 0;
  return {
    headers: ['Option', 'Overall Average', ...(hasFiltered ? ['Filtered Average'] : [])],
    rows: overall.map((d, i) => [
      d.optionText,
      fmtVal(d.avgValue),
      ...(hasFiltered ? [fmtVal(allocationData.filtered[i]?.avgValue)] : [])
    ])
  };
}

function trendExport(trendData) {
  const dates = trendData?.dates || [];
  const series = trendData?.series || [];
  return {
    headers: ['Month', ...series.map(s => s.name)],
    rows: dates.map((dt, i) => [dt, ...series.map(s => fmtVal(s.data?.[i]))])
  };
}

function distributionExport(distributionData) {
  const opts = Object.keys(distributionData || {});
  const buckets = distributionData?.[opts[0]]?.map(b => b.bucket) || [];
  const totals = opts.map(o => (distributionData[o] || []).reduce((s, b) => s + b.count, 0));
  return {
    headers: ['Bucket', ...opts.map(o => o + ' (%)')],
    rows: buckets.map((bucket, bi) => [
      bucket,
      ...opts.map((o, oi) => {
        const count = distributionData[o]?.[bi]?.count || 0;
        return totals[oi] > 0 ? (count / totals[oi] * 100).toFixed(1) : '0.0';
      })
    ])
  };
}

function cohortExport(data, type) {
  const names = Object.keys(data?.overall || {});
  const tryKeys = type === 'rejection'
    ? ['Rejected', 'rejected', 'Rejection', 'rejection', 'RI', 'ri']
    : ['Committed', 'committed', 'Commitment', 'commitment', 'CI', 'ci'];
  const extract = (cohorts) => {
    if (typeof cohorts === 'number') return cohorts;
    for (const k of tryKeys) { if (cohorts?.[k] != null) return cohorts[k]; }
    const vals = Object.values(cohorts || {}).filter(v => typeof v === 'number');
    return vals.length > 0 ? Math.max(...vals) : 0;
  };
  return {
    headers: ['Option', type === 'rejection' ? 'Rejection Index (%)' : 'Commitment Index (%)'],
    rows: names.map(n => [n, fmtVal(extract(data.overall[n]))])
  };
}

function persuadableExport(commitmentTrend, rejectionTrend, option) {
  const built = buildPersuadableSeries(commitmentTrend, rejectionTrend, option);
  if (!built) return { headers: [], rows: [] };
  return {
    headers: ['Month', 'Committed (%)', 'Persuadable (%)', 'Rejecting (%)'],
    rows: built.dates.map((dt, i) => [
      dt, fmtVal(built.committed[i]), fmtVal(built.persuadable[i]), fmtVal(built.rejecting[i])
    ])
  };
}

function singleChoiceExport(chartData) {
  const total = chartData.reduce((s, d) => s + d.count, 0);
  return {
    headers: ['Option', '% of Respondents'],
    rows: chartData.map(d => [d.optionText, total > 0 ? (d.count / total * 100).toFixed(1) : '0.0'])
  };
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

// ── Styles ─────────────────────────────────────────────────────────────────

const panelStyle = {
  background: '#fff', borderRadius: '14px', padding: '24px',
  border: '1px solid #e2e6ea', marginBottom: '20px',
  display: 'grid', gridTemplateColumns: '300px 1fr', gap: '24px'
};

const questionLabelStyle = {
  fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px',
  color: '#8a94a3', marginBottom: '8px'
};

const questionNameStyle = {
  fontSize: '14px', fontWeight: 600, color: '#1a2332'
};

const startDateStyle = {
  marginTop: '12px', fontSize: '12px', color: '#8a94a3'
};

const descriptionStyle = {
  fontSize: '13px', lineHeight: 1.7, color: '#5a6577'
};

const chartsGridStyle = {
  display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'
};

const cardStyle = {
  background: '#fff', borderRadius: '14px',
  border: '1px solid #e2e6ea', overflow: 'hidden',
  transition: 'box-shadow 0.2s'
};

const cardHeaderStyle = {
  padding: '14px 20px', borderBottom: '1px solid #e2e6ea',
  display: 'flex', alignItems: 'center', gap: '10px'
};

const cardAccentStyle = {
  width: '4px', height: '18px', borderRadius: '2px',
  background: 'linear-gradient(180deg, #2ec4b6, #0f4c5c)'
};

const cardTitleStyle = { fontSize: '14px', fontWeight: 600, color: '#1a2332' };

const optionSegStyle = {
  display: 'inline-flex', background: '#f7f8fa', border: '1px solid #e2e6ea',
  borderRadius: '8px', padding: '2px', marginLeft: '14px'
};

const optionBtnStyle = {
  border: 0, background: 'transparent', cursor: 'pointer', fontFamily: 'inherit',
  fontSize: '12px', fontWeight: 600, color: '#5a6577', padding: '4px 12px', borderRadius: '6px'
};

const optionBtnActiveStyle = { background: '#0f4c5c', color: '#fff' };

const optionSelectStyle = {
  marginLeft: '14px', fontFamily: 'inherit', fontSize: '12px', fontWeight: 600,
  color: '#1a2332', background: '#f7f8fa', border: '1px solid #e2e6ea',
  borderRadius: '8px', padding: '5px 8px', cursor: 'pointer', maxWidth: '220px'
};

const exportBtnStyle = {
  marginLeft: 'auto', background: 'transparent', border: '1px solid #e2e6ea',
  borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: 600,
  color: '#8a94a3', cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap'
};
const cardBodyStyle = { padding: '20px' };

const legendStyle = {
  display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap'
};

const legendItemStyle = {
  display: 'flex', alignItems: 'flex-start', gap: '8px',
  cursor: 'default', maxWidth: '400px'
};

const legendDotStyle = {
  width: '12px', height: '12px', borderRadius: '3px',
  flexShrink: 0, marginTop: '2px'
};

const legendTextStyle = {
  fontSize: '13px', color: '#5a6577', lineHeight: 1.4,
  overflow: 'hidden', textOverflow: 'ellipsis',
  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical'
};

const avgDisplayStyle = {
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  gap: '24px', padding: '24px', textAlign: 'center'
};

const avgNumberStyle = {
  fontSize: '56px', fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1
};

const avgMetaStyle = { textAlign: 'left' };
